import {
  JANE_LOKAL_OPT_IN_SCHLUESSEL,
  JANE_URSPRUENGE,
  janeAktiv,
  wirtVonUrsprung,
} from "@janeway/app-sdk/jane";
import { JANE_MARKE_SCHLUESSEL } from "../jane/marke";
import { EMBED_MARKE } from "./embed-vorpaint";

/**
 * Den Platz des Jane-Knopfs schon im ERSTEN Bild freihalten, nicht erst nach der Hydration.
 *
 * Ob Jane erscheint, entscheidet `janeAktiv()` am Ursprung (`useJaneWirt`) — den kennt nur der
 * Browser, beim SSR ist er `""`. `AppLayout` setzte `--jw-jane-knopf-platz` und
 * `--jw-jane-inhalt-platz` deshalb erst nach der Hydration: am Handy sprang ein App-Knopf im Slot
 * `fab` dann um 72 px nach oben, und das Inhaltsende rueckte nach (PIM-Audit 2026-10-22).
 *
 * Zwei Haelften, wie bei `embed-vorpaint.ts`: `AppLayout` markiert seine Wurzel mit
 * `JANE_GEWUENSCHT_MARKE`, sobald die App Jane anfordert (das weiss schon der Server); dieses
 * Skript setzt `JANE_VORAB_MARKE` an `<html>`, wenn der Ursprung Jane traegt. Nur beides zusammen
 * und nicht eingebettet setzt die Regel die Variablen — dieselben Werte, die `AppLayout` danach
 * inline schreibt.
 */

/** Marke an `<html>`: dieser Ursprung traegt Jane (gesetzt vom Vorpaint-Skript). */
export const JANE_VORAB_MARKE = "data-jw-jane-vorab";

/** Marke an der Huellenwurzel (`data-jw-geraeterand`): die App fordert Jane an. */
export const JANE_GEWUENSCHT_MARKE = "data-jw-jane-gewuenscht";

/** Knopf (48 px) + Abstand 1rem + Luft, dazu Rinne und Keksleiste wie am Knopf selbst. */
export const JANE_KNOPF_PLATZ = "calc(3.75rem + var(--jw-rinne, 0px) + var(--jw-cookieleiste-h, 0px))";

/**
 * Platz RECHTS, den eine Leiste am unteren Rand fuer den Jane-Knopf freilaesst (Notes-Formatleiste am Handy):
 * Knopf 48 px + Abstand zum Rand 1rem + 0,25rem Luft, dazu die Rinne wie am Knopf (`AssistentKnopf`,
 * `right-[calc(…+var(--jw-rinne,0px)+1rem)]`). Ohne Jane 0 — gelesen als `pr-[max(0.5rem,var(--jw-jane-rand-rechts,0px))]`.
 * Vorher rechnete notes `min(4.25rem, var(--jw-jane-knopf-platz)*100)` als Schalter aus einer HOEHE.
 */
export const JANE_RAND_RECHTS_VAR = "--jw-jane-rand-rechts";
export const JANE_RAND_RECHTS = "calc(4.25rem + var(--jw-rinne, 0px))";

/** Inhaltsrand unter dem Jane-Knopf; Herleitung bei `JANE_INHALT_PLATZ_VAR` (`AppLayout`). */
export const JANE_INHALT_PLATZ = "calc(4.5rem + var(--jw-bodenleiste-frei, 0px) + var(--jw-cookieleiste-h, 0px))";

/**
 * Die Wirts-Urspruenge, gefiltert mit `janeAktiv` selbst statt nachgebaut — dieselbe Bedingung wie
 * `wirtSchluessel` in `useJaneWirt` (inklusive: die Umgebung hat einen Chat-Ursprung).
 */
function janeUrspruenge(lokalOptIn: boolean): string[] {
  const alle = Object.values(JANE_URSPRUENGE).flatMap((u) => Object.values(u.wirte) as string[]);
  return alle.filter((o) => {
    const wirt = wirtVonUrsprung(o);
    return wirt !== null && JANE_URSPRUENGE[wirt.umgebung].chat !== null && janeAktiv(o, { lokalOptIn });
  });
}

export const JANE_VORAB_CSS =
  `html[${JANE_VORAB_MARKE}]:not([${EMBED_MARKE}]) [data-jw-geraeterand][${JANE_GEWUENSCHT_MARKE}]{` +
  `--jw-jane-knopf-platz:${JANE_KNOPF_PLATZ};` +
  `${JANE_RAND_RECHTS_VAR}:${JANE_RAND_RECHTS};` +
  `--jw-jane-inhalt-platz:${JANE_INHALT_PLATZ}}`;

/**
 * Das Inline-Skript. Kein `toString()` einer Funktion (esbuilds `__name`, siehe
 * `embedVorpaintSkript`). Die Marke bewacht sich selbst: React baut `<html>` bei der Hydration
 * mitunter neu auf und raeumt dabei alles ab, was nicht aus dem Server-Markup stammt
 * (`embed-marke-ueberlebt-den-wurzel-neuaufbau.test.ts`).
 */
export function janeVorpaintSkript(): string {
  const immer = janeUrspruenge(false);
  const mitOptIn = janeUrspruenge(true).filter((o) => !immer.includes(o));
  const marke = JSON.stringify(JANE_VORAB_MARKE);
  return [
    "(function(){try{",
    `var o=location.origin;if(${JSON.stringify(immer)}.indexOf(o)<0){`,
    `if(${JSON.stringify(mitOptIn)}.indexOf(o)<0)return;`,
    `try{if(localStorage.getItem(${JSON.stringify(JANE_LOKAL_OPT_IN_SCHLUESSEL)})!=='1')return}catch(e){return}}`,
    "try{var m=/(?:^#|&)jane=(offen|anruf)(?:&|$)/.exec(location.hash||'');",
    `if(m)sessionStorage.setItem(${JSON.stringify(JANE_MARKE_SCHLUESSEL)},m[1]+'@'+Date.now())}catch(e){}`,
    "var w=document.documentElement;",
    `w.setAttribute(${marke},'');`,
    "if(typeof MutationObserver!=='function')return;",
    `new MutationObserver(function(){if(!w.hasAttribute(${marke}))w.setAttribute(${marke},'')})`,
    `.observe(w,{attributes:true,attributeFilter:[${marke}]});`,
    "}catch(e){}})()",
  ].join("");
}
